import posthog from "posthog-js"
import { posthogEnabled } from "~/components/providers/Posthog"
import { tutorials } from "~/lib/tutorials"

// One tracker per run, shared by Driver tours and hands-on routes. It never
// changes persisted discovery preferences or captures practice content.
export function createTutorialAnalytics(
	tutorialId: keyof typeof tutorials,
	source?: string
) {
	const attemptId = crypto.randomUUID()
	let startedAt: number | undefined
	let ended = false
	let stepIndex: number | undefined
	let stepStartedAt = 0
	let stepFinished = false
	const capture = (
		event: string,
		extra: Record<string, unknown> = {},
		leaving = false
	) => {
		if (!posthogEnabled) return
		const step =
			stepIndex === undefined
				? undefined
				: tutorials[tutorialId].steps[stepIndex]
		posthog.capture(
			event,
			{
				tutorial_id: tutorialId,
				attempt_id: attemptId,
				source,
				automatic:
					source === undefined
						? undefined
						: source === "automatic" || source === "welcome",
				step_id: step?.id,
				step_number: stepIndex === undefined ? undefined : stepIndex + 1,
				total_steps: tutorials[tutorialId].steps.length,
				elapsed_ms:
					startedAt === undefined ? 0 : Math.max(0, Date.now() - startedAt),
				...extra
			},
			leaving ? { transport: "sendBeacon", send_instantly: true } : undefined
		)
	}
	const end = (
		outcome: "completed" | "dismissed" | "exited",
		reason: string,
		leaving = false
	) => {
		if (startedAt === undefined || ended) return
		ended = true
		window.removeEventListener("pagehide", onPageHide)
		capture(`tutorial_${outcome}`, { reason }, leaving)
	}
	const onPageHide = () => end("exited", "page_unloaded", true)
	return {
		start: () => {
			if (startedAt !== undefined || ended) return
			startedAt = Date.now()
			window.addEventListener("pagehide", onPageHide)
			capture("tutorial_started")
		},
		viewStep: (index: number) => {
			if (
				startedAt === undefined ||
				ended ||
				index === stepIndex ||
				!tutorials[tutorialId].steps[index]
			)
				return
			stepIndex = index
			stepStartedAt = Date.now()
			stepFinished = false
			capture("tutorial_step_viewed")
		},
		finishStep: (skipped = false) => {
			if (
				startedAt === undefined ||
				ended ||
				stepIndex === undefined ||
				stepFinished
			)
				return
			stepFinished = true
			capture("tutorial_step_finished", {
				skipped,
				step_elapsed_ms: Math.max(0, Date.now() - stepStartedAt)
			})
		},
		end
	}
}
